import { FC, useEffect, useState } from "react";
import cx from "classnames";
import Typography from "@/components/Typography";
import Button from "@/components/Button";
import { FilterIcon, InfoIcon } from "@/components/Icon";
import Pagination, { MobilePagination } from "@/components/Pagination";
import { useInView } from "react-intersection-observer";
import {
  useResponsive,
  useTrackers,
  useLock,
  useCurrentTrip,
  useFeatureFlags,
  useTripType,
} from "@/hooks";
import { JobType } from "@/fetch/suppliers";
import { EmptyJobsList } from "@/components/EmptyList";
import { Skeleton } from "@/components/Loader";
import Alert from "@/components/Alert";
import MobilePopup from "@/components/MobilePopup";
import JobCard from "./JobCard";
import ExternalJobCard from "./ExternalJobCard";
import styles from "./JobOpportunitiesTab.module.scss";

type Props = {
  onOpenFilters: () => void;
  totalJobsAmount: number;
  jobs: Array<JobType>;
  suggestedJobs: Array<JobType>;
  refetch: () => void;
  isLoading: boolean;
  isFilterSet: boolean;
  onChangePage: (newPage: number) => void;
  defaultJobs: Array<JobType>;
  isError?: boolean;
  page: number;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isRecentJobsLoading?: boolean;
  fetchNextPage?: () => void;
};

const PAGE_SIZE = 10;

const JobOpportunitiesTab: FC<Props> = ({
  onOpenFilters,
  totalJobsAmount,
  jobs,
  suggestedJobs,
  refetch,
  isLoading: isLoadingJobs,
  isFilterSet,
  onChangePage,
  defaultJobs,
  isError,
  page,
  hasNextPage,
  isFetchingNextPage,
  isRecentJobsLoading,
  fetchNextPage,
}) => {
  // const [page, setPage] = useState<number>(1);
  const [isInfoPopupOpen, setIsInfoPopupOpen] = useState<boolean>(false);
  const { isSuperliteTrip, isWHArrivalOnly } = useTripType();
  const { currentTrip } = useCurrentTrip();
  const { isMobile } = useResponsive();
  const { track } = useTrackers();
  const { ref: loaderContainerRef, inView: isLoaderInView } = useInView({
    threshold: 0,
  });
  const {
    flags: { replaceWHJobsWithSLJobs },
  } = useFeatureFlags();

  useEffect(() => {
    track("Jobs Tab Viewed", {
      eventId: "jobs-tab-viewed",
    });
  }, []);

  // useEffect(() => {
  //   onChangePage(page);
  // }, [page]);

  useEffect(() => {
    if (
      !isLoaderInView ||
      !hasNextPage ||
      isRecentJobsLoading ||
      isFetchingNextPage
    )
      return;

    fetchNextPage?.();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoaderInView,
    isRecentJobsLoading,
  ]);

  const isLoading = isLoadingJobs || isRecentJobsLoading;

  const count = Math.ceil(totalJobsAmount / PAGE_SIZE);

  const hasInfoButton =
    ((!isSuperliteTrip && !isWHArrivalOnly && !replaceWHJobsWithSLJobs) ||
      (!isSuperliteTrip && !isWHArrivalOnly && replaceWHJobsWithSLJobs)) &&
    isMobile;
  return (
    <div className={styles.container}>
      {!isMobile && (
        <>
          {!isSuperliteTrip && !isWHArrivalOnly && !replaceWHJobsWithSLJobs && (
            <Alert variant="standard" color="error" className={styles.banner}>
              Please note, this is not an exhaustive list of all of our Partner
              Organisations as they are updated regularly. Our team still works
              on your job match in the background in the lead up to your
              arrival. Our job board is for you to express interest in suitable
              roles only - you are not applying directly for any roles.
              <br />
              Please do not contact these organisations directly, as they are
              our partners and follow specific processes through Global Work &
              Travel.
            </Alert>
          )}

          {!isSuperliteTrip && !isWHArrivalOnly && replaceWHJobsWithSLJobs && (
            <Alert variant="standard" color="info" className={styles.banner}>
              Our Local Jobs board is now available for you to help find
              additional job opportunities. You can search for jobs based on
              location or job type - whether you’re after casual work, part-time
              roles or something to build your resumé. Each listing includes job
              details making it easy to take the next step. New roles are added
              regularly, so check back often. It’s a great way to land that next
              job on your working holiday adventure.
            </Alert>
          )}
        </>
      )}
      {isError ? (
        <>
          <EmptyJobsList variant="error" />
          <Button onClick={() => refetch()} className={styles.reloadButton}>
            Reload
          </Button>
        </>
      ) : isFilterSet ? (
        <>
          {isLoading ? (
            <Skeleton
              variant="text"
              width={140}
              className={styles.titleContainer}
            />
          ) : (
            <div className={styles.titleContainer}>
              {Boolean(jobs.length) && (
                <Typography variant="h6" className={styles.recommendedTitle}>
                  Matched Jobs
                  {hasInfoButton && (
                    <InfoIcon
                      variant="outlined"
                      className={styles.infoIcon}
                      onClick={() => setIsInfoPopupOpen(true)}
                    />
                  )}
                </Typography>
              )}
              <Button
                variant="text"
                className={cx(styles.filterButton, styles.filterButtonMotion)}
                onClick={onOpenFilters}
              >
                <FilterIcon className={styles.filterIcon} />
                Filters
              </Button>
            </div>
          )}
          {isLoading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : (
            <>
              {jobs.length > 0 ? (
                <>
                  {jobs.map((eachJob) =>
                    isSuperliteTrip ||
                    isWHArrivalOnly ||
                    replaceWHJobsWithSLJobs ? (
                      <ExternalJobCard
                        key={eachJob.id}
                        job={eachJob}
                        onSave={() => refetch()}
                      />
                    ) : (
                      <JobCard
                        key={eachJob.id}
                        job={eachJob}
                        onSave={() => refetch()}
                      />
                    )
                  )}
                  {isMobile && (
                    <>
                      {hasNextPage ? (
                        <div ref={loaderContainerRef}>
                          {isFetchingNextPage && (
                            <Skeleton variant="rectangular" height="180px" />
                          )}
                        </div>
                      ) : (
                        <Typography
                          color="text.secondary"
                          textAlign="center"
                          className={styles.listInfo}
                        >
                          You&apos;ve seen it all!
                        </Typography>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <EmptyJobsList
                    variant="search"
                    className={styles.emptyList}
                  />
                  <div className={styles.titleContainer}>
                    <Typography variant="h6" className={styles.discoverTitle}>
                      Discover new opportunities
                    </Typography>
                  </div>
                  {defaultJobs.map((eachJob) =>
                    isSuperliteTrip ||
                    isWHArrivalOnly ||
                    replaceWHJobsWithSLJobs ? (
                      <ExternalJobCard
                        key={eachJob.id}
                        job={eachJob}
                        onSave={() => refetch()}
                      />
                    ) : (
                      <JobCard
                        key={eachJob.id}
                        job={eachJob}
                        onSave={() => refetch()}
                      />
                    )
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {!replaceWHJobsWithSLJobs && suggestedJobs.length > 0 && (
            <div className={styles.titleContainer}>
              <Typography variant="h6" className={styles.recommendedTitle}>
                Recommended for You
                {hasInfoButton && isMobile && (
                  <InfoIcon
                    variant="outlined"
                    className={styles.infoIcon}
                    onClick={() => setIsInfoPopupOpen(true)}
                  />
                )}
              </Typography>
              <Button
                variant="text"
                className={styles.filterButton}
                onClick={onOpenFilters}
              >
                <FilterIcon className={styles.filterIcon} />
                Filters
              </Button>
            </div>
          )}
          {!replaceWHJobsWithSLJobs &&
            suggestedJobs.map((eachJob) => (
              <JobCard
                key={eachJob.id}
                job={eachJob}
                onSave={() => refetch()}
              />
            ))}
          <div
            className={cx(styles.titleContainer, {
              [styles.discoverWithSuggestedTitle]: suggestedJobs.length,
            })}
          >
            {isLoading ? (
              <Skeleton width={140} variant="text" />
            ) : (
              jobs.length > 0 && (
                <Typography variant="h6" className={styles.discoverTitle}>
                  {isSuperliteTrip || isWHArrivalOnly ? (
                    <>
                      Job Opportunities in
                      {isMobile && <br />} {currentTrip?.destination || ""}
                    </>
                  ) : replaceWHJobsWithSLJobs ? (
                    <>
                      Local Jobs in
                      {isMobile && <br />} {currentTrip?.destination || ""}
                    </>
                  ) : (
                    "Discover new opportunities"
                  )}
                  {(suggestedJobs.length === 0 ||
                    isSuperliteTrip ||
                    isWHArrivalOnly ||
                    replaceWHJobsWithSLJobs) && (
                    <>
                      {hasInfoButton && isMobile && (
                        <InfoIcon
                          variant="outlined"
                          className={styles.infoIcon}
                          onClick={() => setIsInfoPopupOpen(true)}
                        />
                      )}
                    </>
                  )}
                </Typography>
              )
            )}
            {(suggestedJobs.length === 0 ||
              isSuperliteTrip ||
              isWHArrivalOnly ||
              replaceWHJobsWithSLJobs) && (
              <Button
                variant="text"
                className={cx(styles.filterButton, styles.filterButtonMotion)}
                onClick={onOpenFilters}
              >
                <FilterIcon className={styles.filterIcon} />
                Filters
              </Button>
            )}
          </div>
          {isLoading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : (
            <>
              {jobs.length > 0 ? (
                <>
                  {jobs.map((eachJob) =>
                    isSuperliteTrip ||
                    isWHArrivalOnly ||
                    replaceWHJobsWithSLJobs ? (
                      <ExternalJobCard
                        key={eachJob.id}
                        job={eachJob}
                        onSave={() => refetch()}
                      />
                    ) : (
                      <JobCard
                        key={eachJob.id}
                        job={eachJob}
                        onSave={() => refetch()}
                      />
                    )
                  )}
                  {isMobile && (
                    <>
                      {hasNextPage ? (
                        <div ref={loaderContainerRef}>
                          {isFetchingNextPage && (
                            <Skeleton variant="rectangular" height="180px" />
                          )}
                        </div>
                      ) : (
                        <Typography
                          color="text.secondary"
                          textAlign="center"
                          className={styles.listInfo}
                        >
                          You&apos;ve seen it all!
                        </Typography>
                      )}
                    </>
                  )}
                </>
              ) : (
                <EmptyJobsList variant="not-available" />
              )}
            </>
          )}
        </>
      )}
      {jobs.length > 0 && (
        <>
          {!isMobile && (
            <Pagination
              className={styles.pagination}
              count={count}
              page={page}
              onChange={(_, newPage) => onChangePage(newPage)}
            />
          )}
        </>
      )}
      {isInfoPopupOpen && isMobile && (
        <MobilePopup onClose={() => setIsInfoPopupOpen(false)}>
          <div className={styles.infoDialogContainer}>
            {!isSuperliteTrip && !isWHArrivalOnly && !replaceWHJobsWithSLJobs && (
              <Typography variant="body1" className={styles.mobilePopupText}>
                Please note, this is not an exhaustive list of all of our
                Partner Organisations as they are updated regularly. Our team
                still works on your job match in the background in the lead up
                to your arrival. Our job board is for you to express interest in
                suitable roles only - you are not applying directly for any
                roles.
              </Typography>
            )}

            {!isSuperliteTrip && !isWHArrivalOnly && replaceWHJobsWithSLJobs && (
              <Typography variant="body1" className={styles.mobilePopupText}>
                Our Local Jobs board is now available for you to help find
                additional job opportunities. You can search for jobs based on
                location or job type - whether you’re after casual work,
                part-time roles or something to build your resumé. Each listing
                includes job details making it easy to take the next step. New
                roles are added regularly, so check back often. It’s a great way
                to land that next job on your working holiday adventure.
              </Typography>
            )}
          </div>
        </MobilePopup>
      )}
    </div>
  );
};

export default JobOpportunitiesTab;
